import { useMemo } from 'react';

import { PlexOverviewFragment, useGetLoggedInUserQuery } from '../../graphql/gen/types';
import { notEmpty } from '../../utils/notEmpty';
import { ReduxPlex } from '../ReduxPlex';
import { useFiltersState } from './useFiltersState';

function useUserSelectedPlexPermalinks(): string[] {
  const [filtersState] = useFiltersState();

  return filtersState.selectedPlexPermalinks;
}

function useApolloPlexes(): PlexOverviewFragment[] {
  const { data } = useGetLoggedInUserQuery();
  const plexes = data?.currentUser?.plexes;
  const memoizedPlexes = useMemo(() => (plexes || []).filter((plex) => !plex.deleted), [plexes]);
  return memoizedPlexes;
}

export function usePlexes(): ReduxPlex[] {
  const userSelectedPlexPermalinks = useUserSelectedPlexPermalinks();
  const apolloPlexes = useApolloPlexes();

  const plexes = useMemo(() => {
    return (
      apolloPlexes
        // Filter out items unsubscribed in current session
        .filter((graphQlPlex) => graphQlPlex.currentUserRelation?.role != null)
        .map((graphQlPlex) => ({
          ...graphQlPlex,
          selected: userSelectedPlexPermalinks.includes(graphQlPlex.permalink),
          notificationsCount: graphQlPlex.notifications?.count || 0,
        }))
    );
  }, [apolloPlexes, userSelectedPlexPermalinks]);

  return plexes;
}

export function useSelectedPlexIds(): string[] {
  const userSelectedPlexPermalinks = useUserSelectedPlexPermalinks();
  const apolloPlexes = useApolloPlexes();

  const selectedPlexIds = useMemo(() => {
    if (userSelectedPlexPermalinks.length > 0) {
      return userSelectedPlexPermalinks
        .map((plexPermalink) => apolloPlexes.find((apolloPlex) => apolloPlex.permalink === plexPermalink)?.id)
        .filter(notEmpty);
    } else {
      return apolloPlexes.map((plex) => plex.id);
    }
  }, [apolloPlexes, userSelectedPlexPermalinks]);

  const plexesIdsAsStr = JSON.stringify(selectedPlexIds);
  // always pass directly selectedPlexIds instead of [selectedPlexIds]
  // eslint-disable-next-line
  const memoSelectedPlexIds = useMemo(() => selectedPlexIds, [plexesIdsAsStr]);

  return memoSelectedPlexIds;
}

export function usePlexesMap(): { [plexId: string]: PlexOverviewFragment } {
  const plexes = useApolloPlexes();

  const plexesMap = useMemo(() => {
    const plexesMap: { [plexId: string]: PlexOverviewFragment } = {};
    plexes.forEach((p) => (plexesMap[p.id] = p));
    return plexesMap;
  }, [plexes]);

  return plexesMap;
}
